import { useT } from 'apprise-frontend-core/intl/language'
import { useTagCache } from 'apprise-frontend-tags/cache'
import { useTimezone } from 'apprise-ui/time/time'
import parse from 'date-fns/parse'
import { ParseIssue } from './model'


export const useParseUtils = () => {

    const t = useT()

    const codecache = useTagCache()

    const time = useTimezone()

    const now = Date.now() //used as a reference date parsing in date-fns.

    const issueOf = <S, T = string>(parse: (_: T) => S, failTest: (_: S, __: T) => boolean, key: string = 'parse.processing_error') => ((value: T | undefined) => ({
        orIssue: (type: ParseIssue['type']) => ({
            about: (field: string) => ({
                on: (item: any) => {

                    if (!value && (value as any) !== 0)
                        return undefined

                    try {

                        const parsed = parse(value!)

                        if (!failTest(parsed, value!))
                            return parsed


                    } catch (error) {

                        console.warn("unexpected parse error", { value, field, error })

                    }


                    const error = new Error(t(key, { value, field, item }))

                    error['type'] = type

                    throw error
                }
            })
        })
    }))

    const self = {

        ...codecache
        ,

        int: issueOf(parseFloat, (v, s) => isNaN(v) || parseInt(s) !== v, "parse.int_error")

        ,

        float: (num: string | number | undefined, options: number | Partial<{
            decimalDigits: number
            replaceInvalidWithUndefined: boolean
        }>={}) => {
            
          const {decimalDigits, replaceInvalidWithUndefined} = typeof options === 'number' ? {decimalDigits:options} : options 


          return  issueOf(num => {

            const parsed = typeof num === 'number' ? num : parseFloat(num?.replace(',', '.'))

              if (isNaN(parsed) && replaceInvalidWithUndefined)
                return undefined!

          
            if (decimalDigits) {

                const pow = Math.pow(10, decimalDigits)

                return Math.round((parsed + Number.EPSILON) * pow) / pow
            }

            return parsed


        }, value => isNaN(value) && (!!value || !replaceInvalidWithUndefined), "parse.number_error")(num === undefined || num === null ? num : `${num}`)
    
    }


        ,


        // takes a date and return the ISO string of its UTC time.
        // if the date is text, it parses it with the first match from a set of formats.
        // if the date is pre-parsed it return as ISO directly.
        date: (date: string | Date | undefined, formats: string[] = [], options: Partial<{

            replaceUndefinedWith: string
            replaceInvalidWithUndefined: boolean
            rejectParsedDates: boolean
           
        }>={}) => {

           
            // either bounces it back (no need for client to guard for it), or takes it to imply now. 
            if (!date)
                return issueOf(t => options.replaceUndefinedWith, () => false)(undefined)
            
    
            // there's nothing that we need to do, the parser is already configured to compensate for the local timezone.
            // but this no longer enforces formats, which may or may not be ok. for example, we cannot tell if a time was actually specified.
            if (date instanceof Date)
                return issueOf(t => date.toISOString(), () => !!options.rejectParsedDates, 'parse.date_preparse_error')(time.format(date,'default_long'))
            


            let parsed: Date | undefined = undefined
            let parsedWith: string | undefined = undefined

            const normalised = (date as string).replace(/\s+/g, ' ').trim();

            for (const format of formats) {

                // this may be invalid or valid UTC/absolute.
                const candidate = parse(normalised, format, now)
                

                const valid = !isNaN(candidate.getTime())

                if (valid) {

                    parsed = candidate
                    parsedWith = format

                    break   // we have out match
                }

            }
            

            if (parsed && parsedWith) {

                // if it included an offset, it used it to shift to UTC/absolute.
                const timezoneAware = /X{1,4}|x{1,4}|z{1,4}|O{1,4}/.test(parsedWith);

                // timezone-naive Dates have been interpreted as local time (eg. in CET +1: 10:40 -> 9:40 )
                // but the time was never intended as local time, so it needs to be shifted 'back' (eg. 9:40 -> 10:40)
            
                if (!timezoneAware)
                    parsed = time.intern(parsed)   
            
                
            }
                


            return issueOf(() => parsed?.toISOString(), () => !parsed && !options.replaceInvalidWithUndefined, "parse.date_error")(date)

        }

    }

    return self
}